import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, ExternalLink, Disc3, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";
import useTranslated from "@/hooks/useTranslated";
import { useAuth } from "@/context/AuthContext";
import { beginSocialPulseLogin, ssoConfigured } from "@/lib/socialpulseOidc";

export default function ArtistDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const bioT = useTranslated(`artist-bio-${slug}`, data ? [data.artist.bio] : []);

  useEffect(() => {
    setData(null);
    api.get(`/artists/${slug}`).then((r) => setData(r.data)).catch(console.error);
  }, [slug]);

  const claim = async () => {
    try {
      const r = await api.post(`/artists/${slug}/claim`);
      setData((d) => ({ ...d, artist: { ...d.artist, claim_pending: true } }));
      toast.success(r.data.message || "Claim submitted for admin review.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Claim failed.");
    }
  };

  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { artist: a, related } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`artist-page-${slug}`}>
      <div className="grid lg:grid-cols-3 gap-10">
        <div>
          <img src={a.image} alt={a.name} className="rounded-3xl w-full h-80 object-cover shadow-xl" />
          <div className="bg-white rounded-2xl border border-slate-100 p-5 mt-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Trust &amp; verification</p>
            <p className="text-sm flex items-center gap-2 font-bold text-slate-800">
              <BadgeCheck className={`w-4 h-4 ${a.verified_status === "verified" ? "text-sky-500" : "text-slate-400"}`} />
              {a.verified_status === "verified" ? "Claimed & verified profile" : "Editorial profile (unclaimed)"}
            </p>
            <p className="text-xs text-slate-500 mt-2">Sources reviewed by the MusicPulse editorial desk. <Link to="/community" className="text-pink-600 font-bold">Suggest a correction</Link></p>
            {a.claimed_by ? (
              <p className="mt-3 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2" data-testid="artist-claimed-badge">
                ✓ Claimed by {a.claimed_by_name || "the artist"}{user?.id === a.claimed_by ? " (you)" : ""}
              </p>
            ) : a.claim_pending ? (
              <p className="mt-3 text-xs font-bold text-amber-700 bg-amber-50 rounded-xl px-3 py-2" data-testid="artist-claim-pending-badge">
                ⏳ Ownership claim under admin review
              </p>
            ) : user ? (
              <button data-testid="artist-claim-btn" onClick={claim} className="mt-3 w-full gradient-btn text-white text-xs font-black px-4 py-2.5 rounded-full">
                Claim this profile
              </button>
            ) : (
              <button
                data-testid="artist-claim-signin-btn"
                onClick={() => (ssoConfigured() ? beginSocialPulseLogin(`/artists/${slug}`) : toast.info("SocialPulse SSO is being configured — check back shortly."))}
                className="mt-3 w-full text-xs font-black px-4 py-2.5 rounded-full border-2 border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors"
              >
                Sign in to claim this profile
              </button>
            )}
          </div>
          {a.official_links?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 mt-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Official links</p>
              {a.official_links.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid="artist-official-link" className="flex items-center gap-2 text-sm font-bold text-pink-600 hover:text-pink-700 py-1">
                  <ExternalLink className="w-4 h-4" /> {l.label}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-2">{a.roles?.join(" · ")}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 flex items-center gap-3 flex-wrap">
            {a.name}
            {a.verified_status === "verified" && <BadgeCheck className="w-8 h-8 text-sky-500" />}
            <PronounceButton text={a.name} />
          </h1>
          <p className="mt-3 text-sm text-slate-500 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-pink-500" /> {a.place}, <Link to={`/places/${a.country_slug}`} className="text-pink-600 font-bold hover:underline">{a.country}</Link></span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-purple-500" /> Active {a.active_years}</span>
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {a.genres?.map((g) => <span key={g} className="px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 text-xs font-bold">{g}</span>)}
          </div>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl" data-testid="artist-bio">{bioT[0] || a.bio}</p>

          <AIContentBlock pageKey={`artist-${slug}`} topic={`${a.name} and the ${a.genres?.[0] || "music"} tradition of ${a.country}`} imgQuery={`${a.genres?.[0] || "music"} ${a.country} live music`} />

          <h2 className="font-display text-2xl font-black text-slate-900 mt-10 mb-4 flex items-center gap-2">
            <Disc3 className="w-5 h-5 text-pink-500" /> Notable Works
          </h2>
          <AIContentBlock pageKey={`artist-${slug}-works`} topic={`The recorded legacy and notable works of ${a.name} — why these recordings matter in ${a.genres?.[0] || "world music"}`} compact />
          <div className="space-y-3" data-testid="artist-works">
            {a.works?.map((w) => (
              <div key={w.title} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center text-pink-600 font-black text-sm">{w.year}</div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">{w.title}</p>
                  <p className="text-xs text-slate-500">{w.type}</p>
                </div>
              </div>
            ))}
          </div>

          {related?.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-black text-slate-900 mt-10 mb-4">Related Artists</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/artists/${r.slug}`} data-testid={`related-artist-${r.slug}`} className="card-lift bg-white rounded-2xl border border-slate-100 overflow-hidden group">
                    <div className="h-24 overflow-hidden"><img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                    <div className="p-3"><p className="font-bold text-xs text-slate-900">{r.name}</p><p className="text-[10px] text-slate-500">{r.country}</p></div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
